import { useId } from 'react';
import FieldLabel from '@components/Common/FieldLabel';
import { Box, Typography } from '@mui/material';
import type {
  ContentEditableEvent
} from 'react-simple-wysiwyg';
import {
  BtnBold,
  BtnBulletList,
  BtnItalic,
  BtnNumberedList,
  Editor,
  EditorProvider,
  Toolbar,
} from 'react-simple-wysiwyg';
import './TextEditor.scss';
import { sanitizeHtml } from '@helpers/utils';

interface TextEditorProps {
  value: string;
  setValue: (value: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  minHeight?: string;
  className?: string;
  /**
   * Optional label above the editor, so a rich-text field can say it is mandatory.
   *
   * Without this the one required field on a form could be the one field with no way to carry a
   * marker: the job description is compulsory, and it had neither an asterisk nor a label.
   */
  label?: string;
  required?: boolean;
}

export const TextEditor = (props: TextEditorProps) => {
  /*
   * The editable region is a contenteditable div, which is NOT a labelable element: `<label for>`
   * does not associate with one, so the label would be decoration and the asterisk would announce
   * nothing. FieldLabel carries an id and the editor points back at it with aria-labelledby,
   * which is the association that does work here.
   */
  const labelId = useId();
  const {
    value,
    setValue,
    readOnly = false,
    placeholder = "Start typing here...",
    minHeight = "20vh",
    className = "",
    label,
    required = false
  } = props;

  function onChange(e: ContentEditableEvent) {
    if (!readOnly) {
      setValue(e.target.value);
    }
  }

  // If in read-only mode and content is empty, show placeholder
  if (readOnly && (!value || value.trim() === '' || value === '<p></p>' || value === '<p><br></p>')) {
    return (
      <Box className={`text-editor-readonly-empty ${className}`}>
        <Typography className="muted-text" style={{ fontStyle: 'italic' }} variant="body2">
          No content available.
        </Typography>
      </Box>
    );
  }

  // If in read-only mode with content, show clean HTML view
  if (readOnly && value) {
    return (
      <Box
        className={`text-editor-readonly ${className}`}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }}
        sx={{
          '& p': { margin: '0 0 8px 0' },
          '& p:last-child': { margin: 0 },
          '& ul, & ol': { paddingLeft: '20px', margin: '8px 0' },
          '& li': { marginBottom: '4px' },
          '& strong': { fontWeight: 600 },
          '& em': { fontStyle: 'italic' },
          lineHeight: 1.6,
          color: 'text.primary'
        }}
      />
    );
  }

  // Edit mode with full editor
  return (
    <Box className={`text-editor-container ${className}`}>
      {label && (
        <FieldLabel id={labelId} required={required} disabled={readOnly}>
          {label}
        </FieldLabel>
      )}
      {/*
        The placeholder is absolutely positioned, so it needs a positioning context that contains
        the editor and NOTHING above it. It used to resolve against `.text-editor-container`,
        which meant anything added there -- a label, for one -- pushed the editor down while the
        placeholder stayed put and drifted into the toolbar.
      */}
      <Box className="text-editor-field">
        <EditorProvider>
          <Editor
            // react-simple-wysiwyg styles its container inline, so only layout
            // can live here; the rest is in TextEditor.scss, which follows the
            // MuiOutlinedInput spec. The class is what those rules hook onto.
            containerProps={{
              className: 'cc-editor',
              style: { minHeight, height: 'auto' },
            }}
            disabled={readOnly}
            value={value}
            onChange={onChange}
            role="textbox"
            aria-multiline="true"
            aria-labelledby={label ? labelId : undefined}
            aria-required={required || undefined}
          >
            <Toolbar>
              <BtnBold />
              <BtnItalic />
              <BtnBulletList />
              <BtnNumberedList />
            </Toolbar>
          </Editor>
        </EditorProvider>
        {!value && (
          <Typography className="text-editor-placeholder" variant="body2">
            {placeholder}
          </Typography>
        )}
      </Box>
    </Box>
  );
};